/**
 * Blok Bestellingen (sessie 73 blok 4): op de laptop rechts naast de kop,
 * smal direct eronder. Inhoud:
 * - de laatste bestelling (status, verloop, zendingkenmerk, track en
 *   trace, Lightspeed-link);
 * - de bezorgvoorkeur;
 * - de accounts: eigen en samengevoegd met hun aantal bestellingen en
 *   laatste besteldatum ("onbekend" als de server het niet weet, 0 alleen
 *   als het echt 0 is). Samengevoegd met "samengevoegd op <datum>",
 *   "automatisch" als het systeem het deed, en de knop "Niet deze klant";
 * - "mogelijk dezelfde klant" als voorstel met "Hoort erbij" en "Niet
 *   deze klant"; bij twee of meer voorstellen "Geen van deze" (alles in
 *   een keer op niet deze klant), en bij meer dan drie staat de lijst
 *   ingeklapt ("N mogelijke dubbele kaarten, tonen") (sessie 171);
 * - "niet deze klant" ingeklapt, per account "terugzetten".
 *
 * Sessie 186: "Mogelijk dezelfde klant" bestaat ook los
 * (:func:`MogelijkDezelfdeKlant`), voor klantkaart v10 boven de tabs.
 *
 * Na elke handeling ververst alleen de kaart van beide klanten en de
 * klantenlijst.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { copilootQueryKey } from "../../../Werkplek/Copiloot/copilootApi";
import { v6Api } from "../../api";
import { veiligeLink } from "../../Rechts/opmaak";
import { v6Sleutels } from "../../sleutels";
import { datumKort } from "../opmaak";
import type { KaartBestelling, KaartLightspeedAccount } from "../types";
import { accountNaam, accountTelling } from "./hulp";
import { LaatsteBestelling } from "./LaatsteBestelling";

type Handeling = {
  soort: "samengevoegd" | "niet_deze_klant" | "ongedaan";
  account: KaartLightspeedAccount;
};

/** Vanaf zoveel voorstellen staat "mogelijk dezelfde klant" ingeklapt. */
const MOGELIJK_INGEKLAPT_VANAF = 4;

function accountSleutel(a: KaartLightspeedAccount): string {
  return `${a.klant_id}:${a.lightspeed_id ?? a.email ?? ""}`;
}

function AccountWoord({ account }: { account: KaartLightspeedAccount }) {
  const url = account.lightspeed_id ? veiligeLink(account.url) : null;
  const woord = accountNaam(account);
  return url ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      {woord}
    </a>
  ) : (
    <span className="naam">{woord}</span>
  );
}

function samenWoord(a: KaartLightspeedAccount): string {
  const op = a.samengevoegd_op ? datumKort(a.samengevoegd_op) : "";
  const basis = op ? `samengevoegd op ${op}` : "samengevoegd";
  return a.door === "systeem" ? `${basis}, automatisch` : basis;
}

/**
 * De handelingen op accounts: samenvoegen, niet deze klant, terugzetten en
 * "Geen van deze". Gedeeld door de accountlijst en het losse blok "Mogelijk
 * dezelfde klant" (sessie 186), zodat beide hetzelfde verversen.
 */
function useAccountHandelingen(klantId: string) {
  const queryClient = useQueryClient();
  const ververs = (andere: string[]) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(klantId) }),
      ...andere.map((id) => queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(id) })),
      queryClient.invalidateQueries({ queryKey: ["v6", "klanten", "lijst"] }),
      // De copiloot van deze klant kijkt naar de accounts: zijn draad ook.
      queryClient.invalidateQueries({
        queryKey: copilootQueryKey({ soort: "klant", id: klantId }),
      }),
    ]);
  const handel = useMutation({
    mutationFn: (h: Handeling) =>
      h.soort === "ongedaan"
        ? v6Api.klantSamenvoegingOngedaan(klantId, h.account.klant_id)
        : v6Api.klantSamenvoegen(klantId, { bij_klant_id: h.account.klant_id, status: h.soort }),
    onSuccess: (_uit, h) => ververs([h.account.klant_id]),
  });
  // "Geen van deze": een voor een via dezelfde route, zodat elke
  // vastlegging zijn eigen audit krijgt. Na afloop (ook als er een
  // misging) ververst de kaart, zodat wat wel lukte meteen weg is.
  const geenVanDeze = useMutation({
    mutationFn: async (lijst: KaartLightspeedAccount[]) => {
      for (const a of lijst) {
        await v6Api.klantSamenvoegen(klantId, {
          bij_klant_id: a.klant_id,
          status: "niet_deze_klant",
        });
      }
    },
    onSettled: (_uit, _fout, lijst) => ververs(lijst.map((a) => a.klant_id)),
  });
  const bezig = handel.isPending ? accountSleutel(handel.variables.account) : null;
  return { handel, geenVanDeze, bezig };
}

type Handelingen = ReturnType<typeof useAccountHandelingen>;

function FoutRegel({ handelingen }: { handelingen: Handelingen }) {
  return handelingen.handel.isError || handelingen.geenVanDeze.isError ? (
    <p className="v6-fout" role="alert">
      Vastleggen lukte niet. Probeer het opnieuw.
    </p>
  ) : null;
}

function MogelijkLijst({
  mogelijk,
  handelingen,
}: {
  mogelijk: KaartLightspeedAccount[];
  handelingen: Handelingen;
}) {
  const { handel, geenVanDeze, bezig } = handelingen;
  const [toonMogelijk, setToonMogelijk] = useState(false);
  // "Geen van deze" vraagt eerst een bevestiging in de pagina (herstel 171).
  const [vraagGeenVanDeze, setVraagGeenVanDeze] = useState(false);
  const ingeklapt = mogelijk.length >= MOGELIJK_INGEKLAPT_VANAF;
  return (
    <div className="kk-mogelijk" data-testid="bestellingen-mogelijk">
      <div className="kop">
        {ingeklapt ? (
          <button
            type="button"
            className="kk-tekstknop kk-mogelijk-kop"
            aria-expanded={toonMogelijk}
            onClick={() => setToonMogelijk((v) => !v)}
          >
            {`${mogelijk.length} mogelijke dubbele kaarten, ${toonMogelijk ? "verbergen" : "tonen"}`}
          </button>
        ) : (
          <small className="kopje">Mogelijk dezelfde klant</small>
        )}
        {mogelijk.length > 1 && !vraagGeenVanDeze ? (
          <span className="kk-mini">
            <button
              type="button"
              disabled={geenVanDeze.isPending || handel.isPending}
              onClick={() => setVraagGeenVanDeze(true)}
            >
              Geen van deze
            </button>
          </span>
        ) : null}
      </div>
      {mogelijk.length > 1 && vraagGeenVanDeze ? (
        <div className="kk-bevestig" role="group" aria-label="Bevestig Geen van deze">
          <small>{`Alle ${mogelijk.length} voorstellen op niet dezelfde klant zetten?`}</small>
          <span className="kk-mini">
            <button
              type="button"
              disabled={geenVanDeze.isPending || handel.isPending}
              onClick={() => {
                setVraagGeenVanDeze(false);
                geenVanDeze.mutate(mogelijk);
              }}
            >
              Ja
            </button>
            <button type="button" onClick={() => setVraagGeenVanDeze(false)}>
              Annuleren
            </button>
          </span>
        </div>
      ) : null}
      {(ingeklapt && !toonMogelijk ? [] : mogelijk).map((a) => {
        // Met Lightspeed-nummer staan e-mail en woonplaats er nog niet: erbij.
        const extra = a.lightspeed_id ? [a.email, a.woonplaats].filter(Boolean) : [];
        const onder = [...extra, ...a.redenen].join(", ");
        return (
          <div key={accountSleutel(a)} className="rij">
            <span className="wat">
              {/* Sessie 188: de naam van de andere kaart naast het nummer. */}
              {a.naam ? (
                <b className="naam" data-testid="mogelijk-naam">
                  {a.naam}
                </b>
              ) : null}
              <AccountWoord account={a} />
              {onder ? <small>{onder}</small> : null}
              <small className="tel">{accountTelling(a)}</small>
            </span>
            <span className="kk-mini">
              <button
                type="button"
                disabled={bezig === accountSleutel(a) || geenVanDeze.isPending}
                aria-label={`${accountNaam(a)}: hoort erbij`}
                onClick={() => handel.mutate({ soort: "samengevoegd", account: a })}
              >
                Hoort erbij
              </button>
              <button
                type="button"
                disabled={bezig === accountSleutel(a)}
                aria-label={`${accountNaam(a)}: niet deze klant`}
                onClick={() => handel.mutate({ soort: "niet_deze_klant", account: a })}
              >
                Niet deze klant
              </button>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Alleen "Mogelijk dezelfde klant", los van de accountlijst (sessie 186).
 * Op klantkaart v10 stond het blok alleen in de tab Contact, dus JP zag het
 * niet zolang hij die tab niet opende; nu staat het boven de tabs. Zonder
 * voorstellen toont het niets.
 */
export function MogelijkDezelfdeKlant({
  klantId,
  accounts,
  className,
}: {
  klantId: string;
  accounts: KaartLightspeedAccount[];
  className?: string;
}) {
  const handelingen = useAccountHandelingen(klantId);
  const mogelijk = accounts.filter((a) => a.status === "mogelijk");
  if (mogelijk.length === 0) return null;
  return (
    <div
      className={`kk-accountlijst${className ? ` ${className}` : ""}`}
      data-testid="mogelijk-dezelfde-klant"
    >
      <MogelijkLijst mogelijk={mogelijk} handelingen={handelingen} />
      <FoutRegel handelingen={handelingen} />
    </div>
  );
}

export function Accountlijst({
  klantId,
  accounts,
  metMogelijk = true,
}: {
  klantId: string;
  accounts: KaartLightspeedAccount[];
  /** Uit als "Mogelijk dezelfde klant" al los op de kaart staat (v10). */
  metMogelijk?: boolean;
}) {
  const handelingen = useAccountHandelingen(klantId);
  const { handel, bezig } = handelingen;
  const [toonNiet, setToonNiet] = useState(false);

  const horen = accounts.filter((a) => a.status === "eigen" || a.status === "samengevoegd");
  const mogelijk = metMogelijk ? accounts.filter((a) => a.status === "mogelijk") : [];
  const niet = accounts.filter((a) => a.status === "niet_deze_klant");
  if (accounts.length === 0) return <p className="stil">Geen Lightspeed-account</p>;

  return (
    <div className="kk-accountlijst">
      {horen.length > 0 ? (
        <ul className="kk-accs" data-testid="bestellingen-accounts" aria-label="Accounts">
          {horen.map((a) => (
            <li key={accountSleutel(a)} className={`kk-acc ${a.status}`}>
              <span className="wat">
                <AccountWoord account={a} />
                <small className="st">
                  {a.status === "eigen" ? "eigen account" : samenWoord(a)}
                </small>
                <small className="tel">{accountTelling(a)}</small>
              </span>
              {a.status === "samengevoegd" ? (
                <span className="kk-mini">
                  <button
                    type="button"
                    disabled={bezig === accountSleutel(a)}
                    aria-label={`${accountNaam(a)}: niet deze klant`}
                    onClick={() => handel.mutate({ soort: "niet_deze_klant", account: a })}
                  >
                    Niet deze klant
                  </button>
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {mogelijk.length > 0 ? <MogelijkLijst mogelijk={mogelijk} handelingen={handelingen} /> : null}
      {niet.length > 0 ? (
        <div className="kk-nietdeze" data-testid="bestellingen-niet-deze">
          <button
            type="button"
            className="kk-tekstknop kk-nietdeze-kop"
            aria-expanded={toonNiet}
            onClick={() => setToonNiet((v) => !v)}
          >
            {`${niet.length} niet deze klant`}
          </button>
          {toonNiet
            ? niet.map((a) => (
                <div key={accountSleutel(a)} className="rij">
                  <span className="wat">
                    <AccountWoord account={a} />
                  </span>
                  <button
                    type="button"
                    className="kk-tekstknop"
                    disabled={bezig === accountSleutel(a)}
                    aria-label={`${accountNaam(a)}: terugzetten`}
                    onClick={() => handel.mutate({ soort: "ongedaan", account: a })}
                  >
                    terugzetten
                  </button>
                </div>
              ))
            : null}
        </div>
      ) : null}
      <FoutRegel handelingen={handelingen} />
    </div>
  );
}

/** Inhoud van het blok: laatste bestelling, bezorgvoorkeur, accounts. */
export function Bestellingen({
  klantId,
  bestelling,
  bezorgvoorkeur,
  accounts,
  compact = false,
}: {
  klantId: string;
  bestelling: KaartBestelling | null;
  bezorgvoorkeur: string | null;
  accounts: KaartLightspeedAccount[];
  compact?: boolean;
}) {
  return (
    <div className={`kk-bestellingen${compact ? " compact" : ""}`} data-testid="bestellingen">
      <LaatsteBestelling bestelling={bestelling} compact={compact} />
      <div className="kk-best-regel">
        <small>Bezorging</small>
        <span>{bezorgvoorkeur ?? <span className="stil">onbekend</span>}</span>
      </div>
      <div className="kk-best-regel kolom">
        <small>Accounts</small>
        <Accountlijst klantId={klantId} accounts={accounts} />
      </div>
    </div>
  );
}
